import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Grid,
  Box,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Divider,
  Chip,
  IconButton,
  alpha,
  Autocomplete,
  TextField,
  Tabs,
  Tab,
  LinearProgress,
  Tooltip,
} from '@mui/material';

import TitleIcon from '@mui/icons-material/Title';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PersonIcon from '@mui/icons-material/Person';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import BusinessIcon from '@mui/icons-material/Business';
import ApartmentIcon from '@mui/icons-material/Apartment';
import EventIcon from '@mui/icons-material/Event';
import FlagIcon from '@mui/icons-material/Flag';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CloseIcon from '@mui/icons-material/Close';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useDispatch, useSelector } from 'react-redux';
import { getAllTags, getAssignableGoals, getCreatedTasks, getDepartmentEmployees, getTaskGoals, getTasks, updateTaskStatus } from '../../redux/features/taskManagementSlice';
import { useSnackbar } from 'notistack';
import GlobalMultiSelectId from '../../components/mui/GlobalMultiSelectId';
import { Category, HelpOutline } from '@mui/icons-material';
import dayjs from 'dayjs';
import GlobalAllTypeFileView from '../../components/Fileupload/GlobalAllTypeFileView';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import HistoryIcon from '@mui/icons-material/History';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChecklistIcon from '@mui/icons-material/Checklist';
import TaskComments from './components/TaskComments';
import TaskActivity from './components/TaskActivity';
import TaskRating from './components/TaskRating';
import TaskSubtasks from './components/TaskSubtasks';

// Priority number -> label + color
const PRIORITY_MAP = {
  1: { label: 'Critical', bg: '#fdecea', color: '#b71c1c', border: '#ef9a9a' },
  2: { label: 'High', bg: '#fff3e0', color: '#e65100', border: '#ffb74d' },
  3: { label: 'Medium', bg: '#fffde7', color: '#f9a825', border: '#fff176' },
  4: { label: 'Low', bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
};

const getPriority = (p) => {
  const key = Number(p);
  return PRIORITY_MAP[key] || { label: p || '-', bg: '#eceff1', color: '#455a64', border: '#cfd8dc' };
};

// techsupport_approval_status ids that mean "this work isn't happening"
const REJECTED_STATUS_ID = 3;
const CANCELLED_STATUS_ID = 6;
// goal_jobs.goal_job_status is a separate, binary enum (0 pending / 1 completed) -
// unrelated to department_task_assignments.assignment_status above.
const GOAL_JOB_STATUS_COMPLETED = 1;

export const TaskDetailsDialog = ({ open, onClose, task, filters, showStatusUpdate = true }) => {
  const [selectedStatus, setSelectedStatus] = useState(task?.raw?.assignment_status ?? '');
  const [tab, setTab] = useState(0);
  // Shown when the user picks Cancelled/Rejected on a task that's still
  // linked to a not-yet-completed PMS goal job - lets them choose whether
  // to delete that job too, instead of leaving it dangling as "pending".
  const [pmsDeletePrompt, setPmsDeletePrompt] = useState(false);
  // Tabs that have been opened at least once. Their (data-fetching) content is
  // mounted on first visit and kept mounted (hidden) afterwards, so switching
  // back does not trigger a re-fetch / spinner.
  const [visited, setVisited] = useState({ 0: true });

  const { statusData, statusLoading, linkedGoals, linkedGoalsLoading, subtasks } = useSelector(
    (state) => state.taskManagement
  );
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const handleStatusChange = (event) => setSelectedStatus(event.target.value);

  const currentUser = JSON.parse(localStorage.getItem('cred')).profile;

  const taskId = task?.raw?.task_id;
  const canRate =
    (currentUser?.name && currentUser.name === task?.raw?.created_by_name) ||
    (currentUser?.name && currentUser.name === task?.raw?.assigned_by_name);

  // Prefer the live count once the Subtasks tab has actually been opened and
  // loaded for this task (it reflects anything just added/toggled in this
  // session); otherwise fall back to the snapshot the task list already
  // carries, so the number shows immediately without needing that tab click.
  const subtasksLoadedForThisTask =
    visited[3] && Array.isArray(subtasks) && subtasks.some((s) => String(s.task_id) === String(taskId));
  const subtaskTotal = subtasksLoadedForThisTask ? subtasks.length : Number(task?.raw?.total_subtasks) || 0;
  const subtaskCompleted = subtasksLoadedForThisTask
    ? subtasks.filter((s) => s.is_completed === 1).length
    : Number(task?.raw?.completed_subtasks) || 0;

  // Mark the active tab as visited.
  useEffect(() => {
    setVisited((v) => (v[tab] ? v : { ...v, [tab]: true }));
  }, [tab]);

  // Reset view state whenever a different task is opened. The dialog instance
  // is reused on the board, so stale tab/status/visited must be cleared.
  useEffect(() => {
    if (open) {
      setTab(0);
      setVisited({ 0: true });
      setSelectedStatus(task?.raw?.assignment_status ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, taskId]);

  // Which PMS goal (if any) this task's assignment is linked to.
  useEffect(() => {
    if (open && taskId) {
      dispatch(getTaskGoals(taskId));
    }
  }, [open, taskId, dispatch]);

  const handleUpdateStatus = (deleteLinkedJob = false) => {
    const payload = {
      task_assignment_id: task?.raw?.task_assignment_id,
      assignment_status: selectedStatus,
      delete_linked_job: deleteLinkedJob,
    };

    dispatch(updateTaskStatus(payload)).then((res) => {
      if (res?.payload?.status === 'success') {
        enqueueSnackbar(
          deleteLinkedJob ? 'Task status updated and linked PMS job deleted' : 'Task status updated successfully',
          { variant: 'success' }
        );
        dispatch(getTasks(filters));
        dispatch(getCreatedTasks(filters));
        onClose();
      } else {
        enqueueSnackbar(res?.payload?.message || 'Something went wrong', {
          variant: 'error',
        });
      }
    });
  };

  // The linked PMS goal-job for this task's assignment, if any (same lookup
  // the "PMS Goal" panel below uses).
  const linkedPmsJob = linkedGoals?.find(
    (l) => String(l.task_assignment_id) === String(task?.raw?.task_assignment_id)
  );

  const handleUpdateStatusClick = () => {
    const isCancelledOrRejected =
      Number(selectedStatus) === CANCELLED_STATUS_ID || Number(selectedStatus) === REJECTED_STATUS_ID;

    // Only worth asking if there's a link, and the job isn't already
    // completed - retroactively deleting finished PMS work doesn't make sense.
    if (isCancelledOrRejected && linkedPmsJob && linkedPmsJob.goal_job_status !== GOAL_JOB_STATUS_COMPLETED) {
      setPmsDeletePrompt(true);
      return;
    }

    handleUpdateStatus(false);
  };

  // Compact DetailItem
  const DetailItem = ({ icon, label, value, gradient, iconBg }) => (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 2,
        height: '100%',
        background: gradient || 'linear-gradient(135deg, #ffffff 0%, #f7f9fc 100%)',
        border: '1px solid',
        borderColor: alpha('#000', 0.06),
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
          borderColor: alpha('#1976d2', 0.2),
        },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: iconBg || 'linear-gradient(135deg, #e3f2fd, #bbdefb)',
            color: '#1976d2',
            '& svg': { fontSize: 14 },
          }}
        >
          {icon}
        </Box>
        <Typography
          fontWeight={600}
          fontSize={11.5}
          color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.3 }}
        >
          {label}
        </Typography>
      </Stack>

      <Typography
        variant="body2"
        sx={{
          color: 'text.primary',
          fontWeight: 500,
          fontSize: 13,
          pl: 0.25,
          lineHeight: 1.7,
          whiteSpace: 'pre-wrap',
          pl: 0.5,
        }}
      >
        {value || '-'}
      </Typography>
    </Box>
  );

  const pInfo = getPriority(task?.raw?.priority);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          background: 'linear-gradient(180deg, #ffffff 0%, #f5f7fb 100%)',
        },
      }}
    >
      {/* Compact Gradient Header */}
      <DialogTitle
        sx={{
          p: 0,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '8px',
                background: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(8px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <TaskAltIcon sx={{ color: '#fff', fontSize: 20 }} />
            </Box>
            <Box>
              <Typography fontWeight={700} fontSize={15}>
                Task Details
              </Typography>
              <Typography fontSize={11} sx={{ opacity: 0.85 }}>
                View and manage task information
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, background: 'transparent', minHeight: { xs: 420, sm: 480 } }}>
        {/* Tab bar */}
        <Box sx={{ borderBottom: '1px solid rgba(0,0,0,0.06)', px: 1.5, background: '#fff' }}>
          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 44,
              '& .MuiTab-root': {
                minHeight: 44,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.82rem',
                color: '#64748B',
              },
              '& .Mui-selected': { color: '#667eea !important' },
              '& .MuiTabs-indicator': { background: 'linear-gradient(135deg, #667eea, #764ba2)', height: 3, borderRadius: 3 },
            }}
          >
            <Tab iconPosition="start" icon={<InfoOutlinedIcon sx={{ fontSize: 17 }} />} label="Details" />
            <Tab iconPosition="start" icon={<ChatBubbleOutlineIcon sx={{ fontSize: 17 }} />} label="Comments" />
            <Tab iconPosition="start" icon={<HistoryIcon sx={{ fontSize: 17 }} />} label="Activity" />
            <Tab iconPosition="start" icon={<ChecklistIcon sx={{ fontSize: 17 }} />} label="Subtasks" />
            {canRate && <Tab iconPosition="start" icon={<StarBorderRoundedIcon sx={{ fontSize: 18 }} />} label="Rating" />}
          </Tabs>
        </Box>

        {visited[1] && (
          <Box sx={{ p: 2, display: tab === 1 ? 'block' : 'none' }}>
            <TaskComments taskId={taskId} />
          </Box>
        )}
        {visited[2] && (
          <Box sx={{ p: 2, display: tab === 2 ? 'block' : 'none' }}>
            <TaskActivity taskId={taskId} />
          </Box>
        )}
        {visited[3] && (
          <Box sx={{ p: 2, display: tab === 3 ? 'block' : 'none' }}>
            <TaskSubtasks taskId={taskId} />
          </Box>
        )}
        {visited[4] && canRate && (
          <Box sx={{ p: 2, display: tab === 4 ? 'block' : 'none' }}>
            <TaskRating
              taskId={taskId}
              createdByName={task?.raw?.created_by_name}
              assignedByName={task?.raw?.assigned_by_name}
            />
          </Box>
        )}

        {tab === 0 && task?.raw && (
          <Box sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <DetailItem
              icon={<TitleIcon sx={{ color: '#fff' }} />}
              label="Title"
              value={task.raw.title}
              gradient="linear-gradient(135deg, #e3f2fd 0%, #ffffff 100%)"
              iconBg="linear-gradient(135deg, #1976d2, #42a5f5)"
            />

            <DetailItem
              icon={<Category sx={{ color: '#fff' }} />}
              label="Category"
              value={task.raw.task_category_name ? task.raw.task_category_name : 'Not specified'}
              gradient="linear-gradient(135deg, #e3f2fd 0%, #ffffff 100%)"
              iconBg="linear-gradient(135deg, #4f4da1, #8a42f5)"
            />

            <DetailItem
              icon={<DescriptionIcon sx={{ color: '#fff' }} />}
              label="Description"
              value={task.raw.description}
              gradient="linear-gradient(135deg, #f3e5f5 0%, #ffffff 100%)"
              iconBg="linear-gradient(135deg, #8e24aa, #ba68c8)"
            />

            <DetailItem
              icon={<AttachFileIcon sx={{ color: '#fff' }} />}
              label="Attachments"
              value={task.raw.attachments?.length > 0 ? `${task.raw.attachments.length} file(s)` : 'No attachments'}
              gradient="linear-gradient(135deg, #e0f7fa 0%, #ffffff 100%)"
              iconBg="linear-gradient(135deg, #00838f, #4dd0e1)"
            />
            <GlobalAllTypeFileView attachmentFiles={task?.raw?.attachments} previewButtonName={'View'} />

            <Divider sx={{ my: 0.5, opacity: 0.6 }}>
              <Chip label="More Info" size="small" sx={{ fontWeight: 600, fontSize: 10.5, height: 20 }} />
            </Divider>

            <Grid container spacing={1.25}>
              <Grid item xs={12} sm={6}>
                <DetailItem
                  icon={<AssignmentIndIcon sx={{ color: '#fff' }} />}
                  label="Assigned By"
                  value={task.raw.assigned_by_name}
                  iconBg="linear-gradient(135deg, #43a047, #81c784)"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DetailItem
                  icon={<PersonIcon sx={{ color: '#fff' }} />}
                  label="Assigned To"
                  value={task.raw.assigned_to_name}
                  iconBg="linear-gradient(135deg, #fb8c00, #ffb74d)"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DetailItem
                  icon={<PersonOutlineIcon sx={{ color: '#fff' }} />}
                  label="Created By"
                  value={task.raw.created_by_name}
                  iconBg="linear-gradient(135deg, #5e35b1, #9575cd)"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DetailItem
                  icon={<BusinessIcon sx={{ color: '#fff' }} />}
                  label="Source Dept"
                  value={task.raw.source_dept_name}
                  iconBg="linear-gradient(135deg, #00897b, #4db6ac)"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DetailItem
                  icon={<ApartmentIcon sx={{ color: '#fff' }} />}
                  label="Target Dept"
                  value={task.raw.target_dept_name}
                  iconBg="linear-gradient(135deg, #3949ab, #7986cb)"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DetailItem
                  icon={<EventIcon sx={{ color: '#fff' }} />}
                  label="Expected Delivery"
                  value={dayjs(task.raw.expected_delivery_date).format('DD-MM-YYYY')}
                  iconBg="linear-gradient(135deg, #e53935, #ef9a9a)"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DetailItem
                  icon={<FlagIcon sx={{ color: '#fff' }} />}
                  label="Priority"
                  iconBg="linear-gradient(135deg, #f4511e, #ffab91)"
                  value={
                    <Chip
                      label={pInfo.label}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: 11,
                        height: 20,
                        bgcolor: pInfo.bg,
                        color: pInfo.color,
                        border: `1px solid ${pInfo.border}`,
                      }}
                    />
                  }
                />
              </Grid>

              {subtaskTotal > 0 && (
                <Grid item xs={12}>
                  <DetailItem
                    icon={<ChecklistIcon sx={{ color: '#fff' }} />}
                    label="Subtasks"
                    iconBg="linear-gradient(135deg, #0891b2, #67e8f9)"
                    value={
                      <Stack spacing={0.75} sx={{ width: '100%' }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Chip
                            label={`${subtaskCompleted}/${subtaskTotal} completed`}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              fontSize: 11,
                              height: 20,
                              bgcolor: subtaskCompleted === subtaskTotal ? '#e8f5e9' : '#eceff1',
                              color: subtaskCompleted === subtaskTotal ? '#2e7d32' : '#455a64',
                            }}
                          />
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#0891b2' }}>
                            {Math.round((subtaskCompleted / subtaskTotal) * 100)}%
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={(subtaskCompleted / subtaskTotal) * 100}
                          sx={{
                            width: '100%',
                            height: 8,
                            borderRadius: 4,
                            bgcolor: '#EEF2F6',
                            '& .MuiLinearProgress-bar': { borderRadius: 4, background: 'linear-gradient(135deg, #0891b2, #67e8f9)' },
                          }}
                        />
                      </Stack>
                    }
                  />
                </Grid>
              )}

              {task?.raw?.task_assignment_id && (
                <Grid item xs={12}>
                  <DetailItem
                    icon={<FlagIcon sx={{ color: '#fff' }} />}
                    label="PMS Goal"
                    iconBg="linear-gradient(135deg, #764ba2, #9575cd)"
                    value={
                      linkedGoalsLoading ? (
                        'Loading...'
                      ) : (() => {
                          const link = linkedGoals?.find(
                            (l) => String(l.task_assignment_id) === String(task.raw.task_assignment_id)
                          );
                          return link ? (
                            <Tooltip
                              title={
                                (link.origin === 'job_to_task'
                                  ? 'Auto-created from this PMS goal job'
                                  : 'Linked to this PMS goal job') +
                                ` - stays in sync (title, due date, completion). Goal: "${link.goal_name}", Job: "${link.goal_job_name}", ${
                                  link.goal_job_status === 1 ? 'job completed' : 'job in progress'
                                }. Click to open PMS in a new tab.`
                              }
                            >
                              <Chip
                                label={link.goal_name}
                                size="small"
                                onClick={() => window.open('/Appraisal-Management/goal', '_blank', 'noopener,noreferrer')}
                                sx={{
                                  fontWeight: 700,
                                  fontSize: 11,
                                  height: 22,
                                  maxWidth: '100%',
                                  cursor: 'pointer',
                                  bgcolor: link.goal_job_status === 1 ? '#e8f5e9' : '#ede7f6',
                                  color: link.goal_job_status === 1 ? '#2e7d32' : '#4527a0',
                                  '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                                }}
                              />
                            </Tooltip>
                          ) : (
                            'Not linked to a PMS goal'
                          );
                        })()
                    }
                  />
                </Grid>
              )}

              {Array.isArray(task?.raw?.tags) && task.raw.tags.length > 0 && (
                <Grid item xs={12}>
                  <DetailItem
                    icon={<LocalOfferIcon sx={{ color: '#fff' }} />}
                    label="Tags"
                    iconBg="linear-gradient(135deg, #0891b2, #67e8f9)"
                    value={
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        {task.raw.tags.map((tag) => (
                          <Chip
                            key={tag.id}
                            label={tag.name}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              fontSize: 11,
                              height: 22,
                              background: `${tag.color}22`,
                              color: tag.color,
                              border: `1px solid ${tag.color}55`,
                            }}
                          />
                        ))}
                      </Stack>
                    }
                  />
                </Grid>
              )}

              {showStatusUpdate && currentUser?.name === task?.raw?.assigned_to_name && (
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      p: 1.25,
                      borderRadius: 2,
                      height: '100%',
                      background: 'linear-gradient(135deg, #e8eaf6 0%, #ffffff 100%)',
                      border: '1px solid',
                      borderColor: alpha('#3f51b5', 0.15),
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(63,81,181,0.12)',
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" mb={0.75}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'linear-gradient(135deg, #3f51b5, #7986cb)',
                          color: '#fff',
                          '& svg': { fontSize: 14 },
                        }}
                      >
                        <AutorenewIcon />
                      </Box>
                      <Typography
                        fontWeight={600}
                        fontSize={11.5}
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: 0.3 }}
                      >
                        Update Status
                      </Typography>
                    </Stack>

                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ fontSize: 13 }}>Status</InputLabel>
                      <Select
                        value={selectedStatus}
                        label="Status"
                        onChange={handleStatusChange}
                        sx={{
                          borderRadius: 1.5,
                          bgcolor: '#fff',
                          fontSize: 13,
                          '& .MuiSelect-select': { py: 0.75 },
                        }}
                      >
                        {statusData?.map((status) => (
                          <MenuItem
                            key={status.approval_status_id}
                            value={status.approval_status_id}
                            sx={{ fontSize: 13 }}
                          >
                            {status.approval_status_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Validation Messages */}
                    <Stack spacing={0.75} sx={{ mt: 1 }}>
                      {!task.raw.assigned_to_name && (
                        <Typography
                          sx={{
                            fontSize: '0.8rem',
                            color: '#d32f2f',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          ⚠ Task not assigned to anyone
                        </Typography>
                      )}
                      {selectedStatus === '' && (
                        <Typography
                          sx={{
                            fontSize: '0.8rem',
                            color: '#d32f2f',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          ⚠ Please select a status
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 1.5,
          background: 'linear-gradient(180deg, #f5f7fb 0%, #eceff5 100%)',
          borderTop: '1px solid rgba(0,0,0,0.05)',
        }}
      >
        <Button
          onClick={onClose}
          size="small"
          startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 12.5,
            borderRadius: 1.5,
            px: 2,
            color: 'text.secondary',
          }}
        >
          Close
        </Button>
        {tab === 0 && showStatusUpdate && currentUser?.name === task?.raw?.assigned_to_name && (
          <Button
            variant="contained"
            size="small"
            disabled={statusLoading || selectedStatus === '' || !task.raw.assigned_to_name}
            onClick={handleUpdateStatusClick}
            startIcon={!statusLoading && <CheckCircleIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 12.5,
              borderRadius: 1.5,
              px: 2.5,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 3px 10px rgba(102,126,234,0.3)',
              '&:hover:not(:disabled)': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4292 100%)',
                boxShadow: '0 5px 14px rgba(102,126,234,0.4)',
              },
              '&:disabled': {
                opacity: 0.6,
              },
            }}
          >
            {statusLoading ? 'Updating...' : 'Update Status'}
          </Button>
        )}
      </DialogActions>

      {/* Cancelled/Rejected while linked to a not-yet-completed PMS job -
          ask whether to delete that job too, instead of silently leaving it
          dangling as "pending" in PMS. */}
      <Dialog open={pmsDeletePrompt} onClose={() => setPmsDeletePrompt(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Delete the linked PMS job too?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            This task is linked to a PMS goal job. Updating the status won't delete it automatically.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button size="small" color="inherit" onClick={() => setPmsDeletePrompt(false)}>
            Cancel
          </Button>
          <Button
            size="small"
            onClick={() => {
              setPmsDeletePrompt(false);
              handleUpdateStatus(false);
            }}
          >
            No, just update status
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={() => {
              setPmsDeletePrompt(false);
              handleUpdateStatus(true);
            }}
          >
            Yes, delete the PMS job
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export const TaskAssignmentDialog = ({ open, onClose, task, confirmSelfAssign, filters }) => {
  const { statusLoading, departmentEmployees, assignableGoals, assignableGoalsLoading, allTags } = useSelector(
    (state) => state.taskManagement
  );
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const currentUser = JSON.parse(localStorage.getItem('cred')).profile;

  const isself = currentUser.name;

  const departmentId = currentUser?.departmentId;

  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // PMS goal linkage: every new assignment must be tied to a goal owned by
  // the assignee - either pick one of their existing goals, or create a
  // minimal one on the spot if they don't have any yet.
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [creatingNewGoal, setCreatingNewGoal] = useState(false);
  const [newGoalName, setNewGoalName] = useState('');
  const [newGoalStartDate, setNewGoalStartDate] = useState('');
  const [newGoalEndDate, setNewGoalEndDate] = useState('');
  const [newGoalTagIds, setNewGoalTagIds] = useState([]);

  useEffect(() => {
    if (open) {
      dispatch(getDepartmentEmployees(departmentId));
      dispatch(getAllTags());
    }
  }, [open, dispatch]);

  // Fetch the selected employee's own goals whenever they change, and reset
  // whatever goal was previously selected/being created.
  useEffect(() => {
    setSelectedGoal(null);
    setCreatingNewGoal(false);
    setNewGoalName('');
    setNewGoalStartDate('');
    setNewGoalEndDate('');
    setNewGoalTagIds([]);

    if (open && selectedEmployee?.userId) {
      dispatch(getAssignableGoals(selectedEmployee.userId));
    }
  }, [selectedEmployee, open, dispatch]);

  // If this employee turns out to have no goals at all, default straight
  // into "create a new goal" instead of showing an empty picker.
  useEffect(() => {
    if (selectedEmployee && !assignableGoalsLoading && assignableGoals?.length === 0) {
      setCreatingNewGoal(true);
    }
  }, [selectedEmployee, assignableGoalsLoading, assignableGoals]);

  // Prefill selectedEmployee when department employees load or when task changes
  useEffect(() => {
    if (!open || !task || !departmentEmployees || departmentEmployees.length === 0) return;

    // Try to find by numeric userId from task.raw.task_assigned_to
    const assignedId = Number(task?.raw?.task_assigned_to ?? task?.raw?.task_assigned_to ?? task?.raw?.task_assigned_to);

    let found = null;
    if (assignedId && !Number.isNaN(assignedId)) {
      found = departmentEmployees.find((e) => Number(e.userId) === assignedId);
    }

    // Fallback: match by assigned_to_name / assigned_to if present
    if (!found) {
      const assignedName = task?.raw?.assigned_to_name || task?.raw?.assigned_to || task?.raw?.assigned_to_name;
      if (assignedName) {
        found = departmentEmployees.find((e) => (e.name || '').toLowerCase() === (assignedName || '').toLowerCase());
      }
    }

    setSelectedEmployee(found || null);
  }, [open, task, departmentEmployees]);

  const hasValidGoalSelection = Boolean(
    selectedGoal || (creatingNewGoal && newGoalName && newGoalStartDate && newGoalEndDate)
  );

  const handleAssign = () => {
    if (!task || !selectedEmployee || !hasValidGoalSelection) return;

    const payload = {
      task_id: task?.raw?.task_id,
      task_assigned_to: Number(selectedEmployee.userId),
      assignment_status: 0,
      ...(selectedGoal
        ? { goal_id: selectedGoal.goal_id }
        : {
            newGoal: {
              name: newGoalName,
              startDate: newGoalStartDate,
              endDate: newGoalEndDate,
              tagIds: newGoalTagIds,
            },
          }),
    };

    dispatch(updateTaskStatus(payload)).then((res) => {
      if (res.payload?.status === 'success') {
        enqueueSnackbar('Task assigned successfully!', { variant: 'success' });
        dispatch(getTasks(filters));
        dispatch(getCreatedTasks(filters));
        onClose();
      } else {
        enqueueSnackbar(res.payload?.message || 'Failed to assign task', { variant: 'error' });
      }
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          background: 'linear-gradient(180deg, #ffffff 0%, #f5f7fb 100%)',
        },
      }}
    >
      {/* Gradient Header */}
      <DialogTitle
        sx={{
          p: 0,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '8px',
                background: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(8px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AssignmentIndIcon sx={{ color: '#fff', fontSize: 20 }} />
            </Box>
            <Box>
              <Typography fontWeight={700} fontSize={15}>
                 Assign to anyone
              </Typography>
              <Typography fontSize={11} sx={{ opacity: 0.85 }}>
                Assign this task to yourself / someone else
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      {/* Body */}
      <DialogContent sx={{ p: 3, background: 'transparent' }}>
        <Stack direction="column" spacing={2} pt={2}>
          <Autocomplete
            options={departmentEmployees || []}
            getOptionLabel={(option) => option.name || ''}
            value={selectedEmployee}
            onChange={(e, newVal) => setSelectedEmployee(newVal)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Assign to"
                placeholder="Select employee"
                variant="outlined"
                fullWidth
              />
            )}
            isOptionEqualToValue={(option, value) => option.userId === value.userId}
          />

          {selectedEmployee && (
            <>
              <Divider sx={{ my: 0.5 }} />
              <Stack direction="row" alignItems="center" spacing={1}>
                <FlagIcon sx={{ fontSize: 18, color: '#764ba2' }} />
                <Typography fontSize={12.5} fontWeight={600} color="text.secondary">
                  PMS Goal (required)
                </Typography>
              </Stack>

              {!creatingNewGoal ? (
                <>
                  <Autocomplete
                    options={assignableGoals || []}
                    loading={assignableGoalsLoading}
                    getOptionLabel={(option) => option.goal_name || ''}
                    value={selectedGoal}
                    onChange={(e, newVal) => setSelectedGoal(newVal)}
                    renderOption={(props, option) => (
                      <li {...props} key={option.goal_id}>
                        <Box>
                          <Typography fontSize={13}>{option.goal_name}</Typography>
                          <Typography fontSize={11} color="text.secondary">
                            Ends {option.goal_end_date}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Attach to goal"
                        placeholder={assignableGoalsLoading ? 'Loading goals...' : "Select this employee's goal"}
                        variant="outlined"
                        fullWidth
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.goal_id === value.goal_id}
                  />
                  <Button
                    size="small"
                    onClick={() => {
                      setSelectedGoal(null);
                      setCreatingNewGoal(true);
                    }}
                    sx={{ textTransform: 'none', fontSize: 12, alignSelf: 'flex-start', px: 0 }}
                  >
                    + Create a new goal instead
                  </Button>
                </>
              ) : (
                <Stack spacing={1.5}>
                  <TextField
                    label="New goal name"
                    value={newGoalName}
                    onChange={(e) => setNewGoalName(e.target.value)}
                    fullWidth
                    size="small"
                  />
                  <Stack direction="row" spacing={1.5}>
                    <TextField
                      label="Start date"
                      type="date"
                      value={newGoalStartDate}
                      onChange={(e) => setNewGoalStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="End date"
                      type="date"
                      value={newGoalEndDate}
                      onChange={(e) => setNewGoalEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: newGoalStartDate || undefined }}
                      fullWidth
                      size="small"
                    />
                  </Stack>
                  <GlobalMultiSelectId
                    label="Goal tags"
                    fullWidth
                    arrayState={newGoalTagIds}
                    arraySetState={setNewGoalTagIds}
                    data={Array.isArray(allTags) ? allTags.filter((tag) => Number(tag?.appraisal_tag_status) === 1) : []}
                    keys={{
                      id: 'appraisal_tag_name',
                      value: 'appraisal_tag_id',
                    }}
                  />
                  {assignableGoals?.length > 0 && (
                    <Button
                      size="small"
                      onClick={() => setCreatingNewGoal(false)}
                      sx={{ textTransform: 'none', fontSize: 12, alignSelf: 'flex-start', px: 0 }}
                    >
                      Use an existing goal instead
                    </Button>
                  )}
                </Stack>
              )}
            </>
          )}
        </Stack>
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          p: 1.5,
          background: 'linear-gradient(180deg, #f5f7fb 0%, #eceff5 100%)',
          borderTop: '1px solid rgba(0,0,0,0.05)',
        }}
      >
        <Button
          onClick={onClose}
          size="small"
          startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 12.5,
            borderRadius: 1.5,
            px: 2,
            color: 'text.secondary',
          }}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          disabled={statusLoading || !selectedEmployee || !hasValidGoalSelection}
          size="small"
          onClick={handleAssign}
          startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 12.5,
            borderRadius: 1.5,
            px: 2.5,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 3px 10px rgba(102,126,234,0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4292 100%)',
              boxShadow: '0 5px 14px rgba(102,126,234,0.4)',
            },
          }}
        >
          {statusLoading ? 'Assigning...' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
